import React from 'react'
import T from 'prop-types'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  CAR_CHOICE_ACCEPTED,
  CAR_CHOICE_ACCEPTED_WITH_REPRICE,
} from 'lib/carChoicesLib'

import ChoiceRecap from 'components/atoms/ChoiceRecap'

import {
  FILTER_CHOICES_ACCEPTED_CHOICE,
  FILTER_CHOICES_NONE_CHOICE,
  FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE,
  FILTER_CHOICES_ALL_CHOICES,
  FILTER_CHOICES_NO_CHOICES,
} from 'hooks/useCarsFilters'

const VARIANT_LIST = 'list'
const VARIANT_DETAILS = 'details'

export default function ChoicesRecap({
  variant = VARIANT_LIST,
  cars = [],
  filteringChoice,
  setFilteringChoice,
  readOnly = false,
}) {
  const { t } = useTranslation()

  const numberFormatter = new Intl.NumberFormat('it-IT')

  const isDetails = variant === VARIANT_DETAILS

  const recapCaps = {
    max: { total: cars.length || 0 },
    selected: {
      allChoices: cars.filter((car) => car.choice !== null).length,
      noChoices: cars.filter((car) => car.choice === null).length,
      accepted: cars.filter((car) => car.choice === CAR_CHOICE_ACCEPTED).length,
      acceptedWithReprice: cars.filter(
        (car) => car.choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE
      ).length,
      total: cars.reduce((previousTotal, car) => {
        return (
          (car.choice !== null
            ? car.choice === CAR_CHOICE_ACCEPTED_WITH_REPRICE
              ? car.reprice
              : car.price + car.fee
            : 0) + previousTotal
        )
      }, 0),
      totalOriginalPrice: cars.reduce(
        (previousTotal, car) =>
          ((car.choice !== null && car.price) || 0) + previousTotal,
        0
      ),
    },
  }

  return (
    <>
      {isDetails && (
        <Typography
          variant="body2"
          color="primaryText"
          sx={{ pb: 1 }}
          data-testid="choices-recap-details-title"
        >
          <strong>{t('carsToBuy.labelTitle.yourSelection')}</strong>
        </Typography>
      )}
      <Stack direction="row" data-testid="choices-recap">
        {!isDetails && (
          <>
            <ChoiceRecap
              id="total"
              title={t('carsToBuy.labelTitle.allCars')}
              value={recapCaps.max.total}
              handleClick={() => setFilteringChoice(FILTER_CHOICES_NONE_CHOICE)}
              border
            />
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <Box pr={2} />
          </>
        )}
        <ChoiceRecap
          id={FILTER_CHOICES_ACCEPTED_CHOICE}
          title={t(`carsToBuy.labelTitle.${CAR_CHOICE_ACCEPTED}`)}
          value={recapCaps.selected.accepted}
          handleClick={
            !isDetails
              ? () => setFilteringChoice(FILTER_CHOICES_ACCEPTED_CHOICE)
              : undefined
          }
          selected={filteringChoice === FILTER_CHOICES_ACCEPTED_CHOICE}
          border
          sx={{ flex: isDetails ? 1 : undefined }}
        />
        <Box pr={1} />
        <ChoiceRecap
          id={FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE}
          title={t('carsToBuy.labelTitle.acceptedWithReprice')}
          value={recapCaps.selected.acceptedWithReprice}
          handleClick={
            !isDetails
              ? () =>
                  setFilteringChoice(
                    FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE
                  )
              : undefined
          }
          selected={
            filteringChoice === FILTER_CHOICES_ACCEPTED_WITH_REPRICE_CHOICE
          }
          border
          sx={{ flex: isDetails ? 1 : undefined }}
        />
        {!isDetails && (
          <>
            <Box pr={1} />
            <ChoiceRecap
              id={FILTER_CHOICES_ALL_CHOICES}
              title={t('carsToBuy.labelTitle.allChoices')}
              value={`${recapCaps.selected.allChoices}`}
              handleClick={() => setFilteringChoice(FILTER_CHOICES_ALL_CHOICES)}
              selected={filteringChoice === FILTER_CHOICES_ALL_CHOICES}
              border
            />
            <Box pr={1} />
            <ChoiceRecap
              id={FILTER_CHOICES_NO_CHOICES}
              title={t('carsToBuy.labelTitle.noChoices')}
              value={`${recapCaps.selected.noChoices}`}
              handleClick={() => setFilteringChoice(FILTER_CHOICES_NO_CHOICES)}
              selected={filteringChoice === FILTER_CHOICES_NO_CHOICES}
              border
            />
            <Box pr={2} />
            <Divider orientation="vertical" variant="middle" flexItem />
            <ChoiceRecap
              dataTestId="choice-recap"
              title={t('carsToBuy.labelTitle.totalValueSelected')}
              value={`€ ${numberFormatter.format(recapCaps.selected.total)}`}
            />
          </>
        )}
      </Stack>
    </>
  )
}

ChoicesRecap.propTypes = {
  variant: T.oneOf([VARIANT_LIST, VARIANT_DETAILS]),
  cars: T.array,
  filteringChoice: T.string,
  setFilteringChoice: T.func,
  readOnly: T.bool,
}
